import { motion } from "framer-motion";
import { ArrowUpRight, Sparkles, Star } from "lucide-react";
import { IMAGES } from "../theme.js";
import { unsplashUrl, unsplashSrcSet } from "../lib/img.js";
import WaveDivider from "./WaveDivider.jsx";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.08 * i, duration: 0.7, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Hero() {
  return (
    <section
      id="topp"
      className="relative isolate overflow-hidden pt-32 pb-[140px] sm:pt-36 sm:pb-[200px]"
    >
      {/* Background image: Scandinavian lake / Vättern */}
      <div className="absolute inset-0 -z-20">
        <img
          src={unsplashUrl(IMAGES.hero, { w: 1600, q: 70 })}
          srcSet={unsplashSrcSet(IMAGES.hero, [800, 1200, 1600, 2000], 70)}
          sizes="100vw"
          alt=""
          width="1600"
          height="1066"
          decoding="async"
          fetchpriority="high"
          className="h-full w-full object-cover"
        />
        {/* Color wash to keep on-brand navy + warmth */}
        <div className="absolute inset-0 bg-gradient-to-br from-navy-950/85 via-navy-900/70 to-navy-800/55" />
        <div className="absolute inset-0 bg-[radial-gradient(80%_60%_at_20%_20%,rgba(218,184,132,0.18),transparent_60%)]" />
      </div>

      {/* Soft grain overlay */}
      <div
        className="pointer-events-none absolute inset-0 -z-10 opacity-[0.18] mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/><feColorMatrix values='0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.5 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
        }}
      />

      <div className="container-x relative grid items-center gap-12 lg:grid-cols-12">
        {/* Copy */}
        <div className="lg:col-span-6">
          <motion.h1
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={1}
            className="mt-6 text-balance font-display text-[44px] leading-[1.02] tracking-tight text-white sm:text-6xl lg:text-[68px]"
          >
            Nästa generations{" "}
            <span className="relative inline-block">
              <span className="relative z-10 text-sand-200">webbyrå</span>
              <svg
                aria-hidden="true"
                viewBox="0 0 240 16"
                className="absolute -bottom-2 left-0 h-3 w-full text-sand-300/80"
                preserveAspectRatio="none"
              >
                <path
                  d="M2 10 C 60 2, 120 16, 238 6"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  fill="none"
                />
              </svg>
            </span>{" "}
            är här.
          </motion.h1>

          <motion.p
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={2}
            className="mt-6 max-w-xl text-balance text-base leading-relaxed text-white/85 sm:text-lg"
          >
            Genom att kombinera mänsklig kreativitet med AI-effektivitet bygger
            vi hemsidor som både ser bra ut och levereras snabbare och billigare
            än du trodde var möjligt.
          </motion.p>

          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={3}
            className="mt-8 flex flex-wrap items-center gap-3"
          >
            <a href="#kontakt" className="btn-sand">
              Starta ditt projekt
              <ArrowUpRight className="h-4 w-4" />
            </a>
            <a
              href="#portfolio"
              className="btn-ghost !border-white/25 !bg-white/10 !text-white hover:!bg-white/15"
            >
              Se vårt arbete
            </a>
          </motion.div>

          {/* Social proof */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={4}
            className="mt-10 flex flex-wrap items-center gap-6 text-white/85"
          >
            <div className="flex -space-x-2">
              {[IMAGES.team.one, IMAGES.team.two, IMAGES.portfolio[0].image_url].map(
                (src, i) => (
                  <img
                    key={i}
                    src={unsplashUrl(src, { w: 80, q: 60 })}
                    alt=""
                    width="36"
                    height="36"
                    loading="lazy"
                    decoding="async"
                    className="h-9 w-9 rounded-full border-2 border-white/80 object-cover"
                  />
                )
              )}
            </div>
            <div className="flex items-center gap-2 text-sm">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className="h-4 w-4 fill-sand-300 text-sand-300"
                  />
                ))}
              </div>
              <span className="font-semibold">5,0</span>
              <span className="text-white/85">· 40+ kunder över hela Sverige</span>
            </div>
          </motion.div>
        </div>

        {/* Mockup composition */}
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="relative lg:col-span-6"
        >
          <div className="relative mx-auto aspect-[3/2] w-full max-w-[960px] lg:-mr-20 lg:max-w-none lg:scale-[1.35] xl:scale-[1.5]">
            {/* Floating chips */}
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -right-2 bottom-2 z-30 hidden rounded-2xl border border-white/30 bg-white/85 px-4 py-3 shadow-soft backdrop-blur sm:block"
            >
              <p className="text-[11px] font-semibold uppercase tracking-widest text-sand-700">
                AI-driven
              </p>
              <p className="mt-1 font-display text-2xl font-semibold text-navy-900">
                3× snabbare
              </p>
              <p className="text-xs text-navy-700">Från idé till lansering</p>
            </motion.div>

            {/* Laptop + telefon mockup */}
            <motion.img
              animate={{ y: [0, -8, 0] }}
              transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
              src="/hero-mockup-laptop.png"
              alt="Förhandsvisning av en hemsida designad av Wetterdesign på laptop och mobil"
              width="1200"
              height="800"
              decoding="async"
              fetchpriority="high"
              className="absolute inset-0 z-10 h-full w-full object-contain"
            />
          </div>
        </motion.div>
      </div>

      {/* Wavy SVG transition into Services */}
      <WaveDivider toColor="#ffffff" />
    </section>
  );
}
